import { Card, CardHeader, CardTable, SpinnerButton } from '@forgeglobal/design-framework'
import {
  GetFilteredOpportunitiesDocument,
  OpportunityFilterInput,
  OpportunityFragment,
  SortDirection,
  TradeIntentType,
  useGetFilteredOpportunitiesQuery,
} from 'graphql/bindings/AdminBindings'
import { OpportunityFormCard } from 'components/opportunities/OpportunityFormCard'
import React, { Fragment } from 'react'
import moment from 'moment'

interface OpportunitiesTableProps {
  opportunityType: TradeIntentType
  tableVariant: 'issuer' | 'account' | 'dashboard'
  header: string
  owners?: number[]
  companyId?: number
}

const issuerColDefs = [
  {
    name: 'account',
    label: 'Account',
    minWidth: 180,
  },
  {
    name: 'size_or_shares',
    label: 'Size/Shares',
    minWidth: 150,
  },
  {
    name: 'price_or_valuation',
    label: 'Price/Valuation',
    minWidth: 150,
  },
  {
    name: 'owners',
    label: 'Owners',
    minWidth: 400,
    priority: 2,
  },
  {
    name: 'last_renewed',
    label: 'Last renewed',
    minWidth: 125,
    priority: 3,
  },
  {
    name: 'fee',
    label: 'Fee',
    minWidth: 150,
    priority: 4,
  },
  {
    name: 'status',
    label: 'Status',
    minWidth: 100,
    priority: 3,
  },
  {
    name: 'entity',
    label: 'Entity',
    minWidth: 150,
    priority: 6,
  },
  {
    name: 'structure',
    label: 'Structure',
    minWidth: 150,
    priority: 6,
  },
  {
    name: 'certainty',
    label: 'Certainty',
    minWidth: 150,
    priority: 7,
  },
  {
    name: 'createDealButton',
    label: 'Deal',
    minWidth: 175,
    isLabelHidden: true,
    fixedSize: true,
  },
  {
    name: 'notes',
    label: 'Notes',
    minWidth: 24,
    isLabelHidden: false,
    fixedSize: true,
  },
]

const dashboardColDefs = [
  {
    name: 'company_name',
    label: 'Issuer',
    minWidth: 150,
  },
]

const NUM_OPPORTUNTIES_DISPLAYED = 10

export const OpportunitiesTable: React.FC<OpportunitiesTableProps> = ({
  opportunityType,
  header,
  owners,
  companyId,
  tableVariant,
}) => {
  const defaultTableVars = {
    owners,
    opportunityType,
    filter: {
      expired: false,
      type: opportunityType,
      owners: owners ?? undefined,
      companyId: companyId ?? undefined,
      sortField: 'date',
      sortDirection: SortDirection.Descending,
      status: 'active',
    } as OpportunityFilterInput,
    limit: NUM_OPPORTUNTIES_DISPLAYED,
  }

  const variables = { ...defaultTableVars, offset: 0 }

  const { data, loading, refetch } = useGetFilteredOpportunitiesQuery({
    variables,
    fetchPolicy: 'network-only',
  })

  const opportunities = data?.opportunities.data ?? []
  const offset = data?.opportunities.offset ?? 0
  const sortedOpportunities = opportunities.sort((a: OpportunityFragment, b: OpportunityFragment) => {
    return moment(a.last_refreshed_at).isBefore(moment(b.last_refreshed_at)) ? 1 : -1
  })

  const count = data?.opportunities.count ?? 0

  const columnDefs = tableVariant === 'issuer' ? issuerColDefs : [...dashboardColDefs, ...issuerColDefs]

  return (
    <Fragment>
      <CardTable
        colDefs={columnDefs}
        header={header}
        count={count}
        onPageChange={(newOffset: number) => {
          refetch({
            ...defaultTableVars,
            offset: newOffset,
          })
        }}
        pageSize={NUM_OPPORTUNTIES_DISPLAYED}
        offset={offset}>
        {sortedOpportunities.map((opportunity: OpportunityFragment) => (
          <OpportunityFormCard
            key={opportunity.id}
            opportunity={opportunity}
            tableVariant={tableVariant}
            refetchOnCancelMutation={[{ query: GetFilteredOpportunitiesDocument, variables }]}
          />
        ))}
        {sortedOpportunities.length === 0 && !loading && (
          <Card>
            <CardHeader title={'None'} />
          </Card>
        )}
        {loading ? (
          <Card>
            <CardHeader
              title={
                <SpinnerButton
                  variant="text"
                  color="primary"
                  type="submit"
                  fullWidth
                  loading={true}
                  caption="Loading"
                />
              }
            />
          </Card>
        ) : null}
      </CardTable>
    </Fragment>
  )
}
